import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * Pure Three.js hero: animated node network + signal particles.
 * No R3F to avoid JSX intrinsic element conflicts with React 19.
 */
const ACCENTS = [
  new THREE.Color("#00F0FF"), // cyan
  new THREE.Color("#007AFF"), // electric blue
  new THREE.Color("#CCFF00"), // lime
  new THREE.Color("#D946EF"), // magenta
  new THREE.Color("#FFB800"), // amber
];

const NODE_COUNT = 95;
const RADIUS = 14;
const SIGNAL_COUNT = 48;

function makeNetwork() {
  const positions = new Float32Array(NODE_COUNT * 3);
  const colors = new Float32Array(NODE_COUNT * 3);
  for (let i = 0; i < NODE_COUNT; i++) {
    const phi = Math.acos(2 * Math.random() - 1);
    const theta = Math.random() * Math.PI * 2;
    const r = RADIUS * (0.4 + 0.6 * Math.random());
    positions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.55;
    positions[i * 3 + 2] = r * Math.cos(phi) * 0.6;
    const c = ACCENTS[i % ACCENTS.length];
    colors[i * 3 + 0] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }

  // near-neighbor lines
  const connIdx = [];
  const maxDist = 4.6;
  for (let i = 0; i < NODE_COUNT; i++) {
    let linked = 0;
    for (let j = i + 1; j < NODE_COUNT && linked < 3; j++) {
      const dx = positions[i * 3] - positions[j * 3];
      const dy = positions[i * 3 + 1] - positions[j * 3 + 1];
      const dz = positions[i * 3 + 2] - positions[j * 3 + 2];
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (d < maxDist) {
        connIdx.push(i, j);
        linked++;
      }
    }
  }
  const linePositions = new Float32Array(connIdx.length * 3);
  for (let k = 0; k < connIdx.length; k++) {
    const idx = connIdx[k];
    linePositions[k * 3] = positions[idx * 3];
    linePositions[k * 3 + 1] = positions[idx * 3 + 1];
    linePositions[k * 3 + 2] = positions[idx * 3 + 2];
  }

  const nodesGeo = new THREE.BufferGeometry();
  nodesGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  nodesGeo.setAttribute("color", new THREE.BufferAttribute(colors, 3));

  const linesGeo = new THREE.BufferGeometry();
  linesGeo.setAttribute("position", new THREE.BufferAttribute(linePositions, 3));

  return { nodesGeo, linesGeo };
}

function makeSignals() {
  const meta = [];
  for (let i = 0; i < SIGNAL_COUNT; i++) {
    meta.push({
      ax: (Math.random() - 0.5) * 22,
      ay: (Math.random() - 0.5) * 10,
      az: (Math.random() - 0.5) * 14,
      bx: (Math.random() - 0.5) * 22,
      by: (Math.random() - 0.5) * 10,
      bz: (Math.random() - 0.5) * 14,
      speed: 0.15 + Math.random() * 0.25,
      offset: Math.random(),
      color: ACCENTS[Math.floor(Math.random() * ACCENTS.length)],
    });
  }
  const positions = new Float32Array(SIGNAL_COUNT * 3);
  const colors = new Float32Array(SIGNAL_COUNT * 3);
  meta.forEach((m, i) => {
    colors[i * 3] = m.color.r;
    colors[i * 3 + 1] = m.color.g;
    colors[i * 3 + 2] = m.color.b;
  });
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return { geo, meta };
}

export default function HeroCanvas() {
  const wrapRef = useRef(null);

  useEffect(() => {
    const mount = wrapRef.current;
    if (!mount) return;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#050505");
    scene.fog = new THREE.Fog("#050505", 18, 32);

    const camera = new THREE.PerspectiveCamera(55, width / height, 0.1, 100);
    camera.position.set(0, 0, 18);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);
    renderer.domElement.style.display = "block";

    // Network
    const { nodesGeo, linesGeo } = makeNetwork();
    const nodesMat = new THREE.PointsMaterial({
      size: 0.14,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      sizeAttenuation: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const nodes = new THREE.Points(nodesGeo, nodesMat);

    const linesMat = new THREE.LineBasicMaterial({
      color: new THREE.Color("#6ee7ff"),
      transparent: true,
      opacity: 0.18,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const lines = new THREE.LineSegments(linesGeo, linesMat);

    const networkGroup = new THREE.Group();
    networkGroup.add(nodes);
    networkGroup.add(lines);
    scene.add(networkGroup);

    // Signals
    const { geo: signalsGeo, meta } = makeSignals();
    const signalsMat = new THREE.PointsMaterial({
      size: 0.26,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      sizeAttenuation: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const signals = new THREE.Points(signalsGeo, signalsMat);
    const signalsGroup = new THREE.Group();
    signalsGroup.add(signals);
    scene.add(signalsGroup);

    // Resize handler
    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    // Pointer parallax
    let targetX = 0;
    let targetY = 0;
    const onPointerMove = (e) => {
      const rect = mount.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / rect.width - 0.5;
      const ny = (e.clientY - rect.top) / rect.height - 0.5;
      targetX = nx * 0.6;
      targetY = -ny * 0.3;
    };
    mount.addEventListener("pointermove", onPointerMove);

    const clock = new THREE.Clock();
    let frame;
    const render = () => {
      const t = clock.getElapsedTime();
      networkGroup.rotation.y = t * 0.05 + targetX;
      networkGroup.rotation.x = Math.sin(t * 0.12) * 0.08 + targetY;
      signalsGroup.rotation.y = networkGroup.rotation.y;
      signalsGroup.rotation.x = networkGroup.rotation.x;
      linesMat.opacity = 0.14 + 0.08 * Math.sin(t * 0.8);

      // animate signals
      const pos = signalsGeo.attributes.position.array;
      for (let i = 0; i < SIGNAL_COUNT; i++) {
        const m = meta[i];
        const phase = (t * m.speed + m.offset) % 1;
        pos[i * 3] = m.ax + (m.bx - m.ax) * phase;
        pos[i * 3 + 1] = m.ay + (m.by - m.ay) * phase;
        pos[i * 3 + 2] = m.az + (m.bz - m.az) * phase;
      }
      signalsGeo.attributes.position.needsUpdate = true;

      renderer.render(scene, camera);
      frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
      mount.removeEventListener("pointermove", onPointerMove);
      mount.removeChild(renderer.domElement);
      renderer.dispose();
      nodesGeo.dispose();
      nodesMat.dispose();
      linesGeo.dispose();
      linesMat.dispose();
      signalsGeo.dispose();
      signalsMat.dispose();
    };
  }, []);

  return (
    <div ref={wrapRef} className="absolute inset-0" data-testid="hero-canvas-wrap">
      <div
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 40%, rgba(5,5,5,0.85) 85%)",
        }}
      />
    </div>
  );
}
